import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useRegionContext } from 'context/RegionContext';
import { Path } from 'pages/Map/MyMap/Map/components/ZoneMap/components/Path';
import { NodeList } from 'pages/Map/MyMap/Map/components/ZoneMap/components/Path/path-points-utils/types';
import { mapScales } from 'pages/Map/MyMap/Map/constants';
import { ExpandedStation, Position, Station, Zone } from 'pages/Map/types';
import { MapState, ZoneContextProviderType, ZoneContextType } from './types';
import {
    getHighestAbsolutePosition,
    getPathsDataFromZone,
    getSectionsData,
    getStation,
    getStationsDataFromZone,
    updatePathsData,
    updateStationsData
} from './utils';
import { MapSearchContext } from 'context/MapSearchContext';
import useMapSearch from 'hooks/search/useMapSearch';
import { Direction } from 'types';
import { useSideNavContext } from 'context/SideNavContext';
import { useLocation } from 'react-router-dom';
import { useMapParams } from 'hooks/useMapParams';
import { useCustomNavigate } from 'hooks/useCustomNavigate';

export const defaultZoneContextType: ZoneContextType = {
    zone: {} as unknown as Zone,
    setSelectedStation: () => {},
    setSelectedPath: () => {},
    stations: [],
    paths: [],
    highestAbsolutePosition: {
        x: 0,
        y: 0
    },
    sections: [],
    setExpandedStation: () => {},
    setPreviousExpandedStation: () => {},
    pathNodeCollection: {
        current: {
            pathNodeList: { nodeX: [], nodeY: [] },
            pathIds: []
        }
    },
    scale: mapScales.INITIAL_SCALE,
    setScale: () => {},
    position: { x: 0, y: 0 },
    setPosition: () => {},
    resetPathNodeCollection: () => {},
    mapState: {
        current: {
            isPanning: false,
            scrollDirection: Direction.NONE
        }
    },
    expandedPathDescription: undefined,
    setExpandedPathDescription: () => {},
    expandedDescriptionStation: undefined,
    setExpandedDescriptionStation: () => {},
    isZoneFirstTimeRender: false,
    setIsZoneFirstTimeRender: () => {}
};

export const ZoneContext = createContext<ZoneContextType>(defaultZoneContextType);

export const useZoneContext = () => useContext(ZoneContext);

/**
 * ZoneContextProvider provides zone context.
 */
export const ZoneContextProvider = ({ children }: ZoneContextProviderType) => {
    const { region, readonly, currentZone: zone, isMapBuilder } = useRegionContext();
    const { setActiveSearchId } = useContext(MapSearchContext);
    const { hasOwnSearchResult } = useMapSearch();
    const [selectedStation, setSelectedStation] = useState<Station>();
    const [selectedPath, setSelectedPath] = useState<Path>();
    const pathNodeCollection = useRef<{ pathNodeList: NodeList; pathIds: string[] }>({
        pathNodeList: { nodeX: [], nodeY: [] },
        pathIds: []
    });
    const [expandedStation, setExpandedStation] = useState<ExpandedStation>();
    const [isZoneFirstTimeRender, setIsZoneFirstTimeRender] = useState(false);
    const [previousExpandedStation, setPreviousExpandedStation] = useState<ExpandedStation>();
    const resetPathNodeCollection = useCallback(() => {
        pathNodeCollection.current = {
            pathNodeList: { nodeX: [], nodeY: [] },
            pathIds: []
        };
    }, []);
    const initialStations = useMemo(() => {
        const stations = getStationsDataFromZone(zone).filter(station => !!station);
        return updateStationsData(stations);
    }, [zone]);
    const [stations, setStations] = useState<Station[]>(initialStations);
    const initialPaths = useMemo(() => getPathsDataFromZone(zone, stations), [zone, stations]);
    const paths = useMemo(() => {
        resetPathNodeCollection();
        return updatePathsData(initialPaths, stations, pathNodeCollection, expandedStation, isMapBuilder);
    }, [initialPaths, stations, expandedStation]);
    const highestAbsolutePosition = useMemo(() => getHighestAbsolutePosition(stations, expandedStation), [stations, expandedStation]);
    const activeStation = useMemo(
        () => (region.position?.station ? getStation(stations, region.position.station) : undefined),
        [region, stations]
    );
    const sections = useMemo(
        () => (zone.sections ? getSectionsData(zone.sections, stations, expandedStation) : []),
        [stations, zone, expandedStation]
    );
    const { station, toMapQueryString, subZone, scale: initialScale, position: initialPosition } = useMapParams();
    const [scale, setScale] = useState<number>(initialScale);
    const [position, setPosition] = useState<Position>({ x: initialPosition.x, y: initialPosition.y });
    const mapState = useRef<MapState>({ isPanning: false, scrollDirection: Direction.NONE });
    const [expandedPathDescription, setExpandedPathDescription] = useState<Path>();
    const [expandedDescriptionStation, setExpandedDescriptionStation] = useState<Station>();
    const { setExpandedState } = useSideNavContext();
    const { navigate } = useCustomNavigate();
    const location = useLocation();

    const onExpandedStationChange = useCallback(
        (station: ExpandedStation) => {
            resetPathNodeCollection();
            setPreviousExpandedStation(expandedStation);
            setExpandedStation(station);
            navigate({ pathname: location.pathname, search: toMapQueryString({ subZone: station?.id || '' }) }, { replace: true });
        },
        [expandedStation, toMapQueryString]
    );

    /**
     * Updates station positions based on expanded and previous expanded station positions
     */
    const updatePositionWithExpandedStations = () => {
        setStations(stations =>
            stations.map(station => {
                station.setRelativePositionWithExpandedStation(expandedStation);
                station.setRelativePositionWithPreviousExpandedStation(expandedStation, previousExpandedStation);
                return station;
            })
        );
    };

    useEffect(() => {
        setStations(updateStationsData(initialStations));
        setIsZoneFirstTimeRender(true);
        onExpandedStationChange(undefined);
    }, [initialStations]);

    useEffect(() => {
        updatePositionWithExpandedStations();
    }, [expandedStation]);

    const onStationSelected = useCallback(
        (station: Station | undefined) => {
            if (!readonly) {
                setSelectedStation(station);
                if (station) {
                    setExpandedState(false);
                    mapState.current.lastInteractedStation = station;
                    hasOwnSearchResult(station.id) && setActiveSearchId(station.id);
                }
                navigate({ pathname: location.pathname, search: toMapQueryString({ station: station?.id || '' }) }, { replace: true });
            }
        },
        [setSelectedStation, setActiveSearchId, hasOwnSearchResult, toMapQueryString]
    );

    const onPathSelected = useCallback(
        (path: Path | undefined) => {
            if (!readonly) {
                setSelectedPath(path);
            }
        },
        [setSelectedPath]
    );

    // opens station with deep link
    useEffect(() => {
        const selectedStation = getStation(stations, station);
        if (!selectedStation) return;
        setExpandedState(false);
        onStationSelected(selectedStation);
    }, [station, stations]);

    // opens suzbone with deep link
    useEffect(() => {
        const selectedSubZone = subZone && getStation(stations, subZone);
        if (!selectedSubZone) return;
        onExpandedStationChange(selectedSubZone as ExpandedStation);
    }, [subZone, stations]);

    return (
        <ZoneContext.Provider
            value={{
                zone,
                selectedStation,
                setSelectedStation: onStationSelected,
                selectedPath,
                setSelectedPath: onPathSelected,
                stations,
                highestAbsolutePosition,
                activeStation,
                paths,
                sections,
                expandedStation,
                setExpandedStation: onExpandedStationChange,
                pathNodeCollection,
                scale,
                setScale,
                position,
                setPosition,
                resetPathNodeCollection,
                previousExpandedStation,
                setPreviousExpandedStation,
                mapState,
                expandedPathDescription,
                setExpandedPathDescription,
                expandedDescriptionStation,
                setExpandedDescriptionStation,
                isZoneFirstTimeRender,
                setIsZoneFirstTimeRender
            }}
        >
            {children}
        </ZoneContext.Provider>
    );
};
